/*
 * Мгновенные запросы к Prometheus кластера через proxy бэкенда `/prometheus/<кластер>/api`
 * (его ведёт модуль argocdInstances по каталогу; basic-auth подставляет бэкенд).
 * Плагин Roadie свой API-клиент не экспортирует, а нам нужны только векторы.
 */
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type PromSample = { metric: Record<string, string>; value: number };

export async function queryInstant(
  apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  cluster: string,
  query: string,
): Promise<PromSample[]> {
  const proxy = await apis.discoveryApi.getBaseUrl('proxy');
  const url = `${proxy}/prometheus/${encodeURIComponent(
    cluster,
  )}/api/query?query=${encodeURIComponent(query)}`;
  const response = await apis.fetchApi.fetch(url);
  if (!response.ok) {
    throw new Error(`Prometheus ${cluster}: HTTP ${response.status}`);
  }
  const body = await response.json();
  if (body.status !== 'success') {
    throw new Error(`Prometheus ${cluster}: ${body.error ?? body.status}`);
  }
  return (body.data?.result ?? []).map(
    (r: { metric: Record<string, string>; value: [number, string] }) => ({
      metric: r.metric,
      value: Number(r.value[1]),
    }),
  );
}

const DATASTORES_NS = 'datastores';

/** Базы кластера CloudNativePG: имя и размер (служебные postgres/template* не считаем). */
export const postgresDatabasesQuery = (name: string) =>
  `max by (datname) (cnpg_pg_database_size_bytes{namespace="${DATASTORES_NS}",pod=~"${name}-[0-9]+",datname!~"template.*|postgres"})`;

/** Топики Kafka (Strimzi) без внутренних. */
export const kafkaTopicsQuery = (name: string) =>
  `count(count by (topic) (kafka_topic_partitions{namespace="${DATASTORES_NS}",strimzi_io_cluster="${name}",topic!~"__.*"}))`;

/** Топики Kafka с числом партиций (kafka-exporter), включая созданные не через KafkaTopic. */
export const kafkaTopicPartitionsQuery = (name: string) =>
  `max by (topic) (kafka_topic_partitions{namespace="${DATASTORES_NS}",strimzi_io_cluster="${name}",topic!~"__.*"})`;

/** Живые брокеры Kafka. */
export const kafkaBrokersQuery = (name: string) =>
  `count(kafka_server_replicamanager_leadercount{namespace="${DATASTORES_NS}",strimzi_io_cluster="${name}"})`;

export const formatBytes = (bytes: number): string => {
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

/* --- Feast кластера (namespace feast): материализация, online store, сервинг --- */

/** Время последней успешной материализации (CronJob feast-materialize), unix-секунды. */
export const feastLastMaterializeQuery = () =>
  `max(kube_cronjob_status_last_successful_time{namespace="feast",cronjob="feast-materialize"})`;

/** Неудачные Job'ы материализации за сутки (kube-state-metrics хранит по failedJobsHistoryLimit). */
export const feastMaterializeFailedQuery = () =>
  `count(kube_job_status_failed{namespace="feast",job_name=~"feast-materialize-.*"} > 0 and on (job_name) (time() - kube_job_created{namespace="feast"} < 24*3600)) or vector(0)`;

/** Строки в таблицах online store по feature view: таблицы <проект>_<feature view> (CNPG custom query). */
export const feastOnlineRowsQuery = () =>
  `max by (relname) (cnpg_feast_online_n_live_tup{namespace="feast"})`;

/** Записей в online store за сутки по feature view. */
export const feastOnlineWritesQuery = () =>
  `sum by (relname) (increase(cnpg_feast_online_n_tup_ins{namespace="feast"}[1d]) + increase(cnpg_feast_online_n_tup_upd{namespace="feast"}[1d]))`;

/** Запросов get-online-features в секунду (feature server). */
export const feastOnlineRpsQuery = () =>
  `sum(rate(feast_feature_server_request_total{namespace="feast",exported_endpoint="/get-online-features"}[15m])) or vector(0)`;

/** Доля признаков, не найденных в online store, за 15 минут. */
export const feastNotFoundRatioQuery = () =>
  `sum(rate(feast_online_features_status_total{namespace="feast",status="not_found"}[15m])) / clamp_min(sum(rate(feast_online_features_status_total{namespace="feast"}[15m])), 0.001)`;

/** Серверы Feast: доступные реплики по Deployment'ам. */
export const feastServersQuery = () =>
  `sum by (deployment) (kube_deployment_status_replicas_available{namespace="feast",deployment=~"feast-(registry|online|offline|ui)"})`;
